import { makeRequest } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer.ts';
import * as ApiV1 from '@dealstack/shared/lib/common/dataExchange/apiV1.ts';
import { HTTP_METHOD } from '@dealstack/shared/lib/common/dataExchange/common.ts';
import {
	ApplicationType,
	ArticleRes,
	AuthorRes,
	CategoryRes,
	EntrypointArticlesRes,
} from '@dealstack/shared/lib/generated';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn.ts';
import type { ClientPerspective } from '@sanity/client';

export const getArticles = async (
	endpoint: string,
	args: {
		dataset: ApplicationType;
		perspective?: ClientPerspective;
		language?: string;
		refId?: string;
		limit?: number;
		searchKeyword?: string;
	}
) => {
	const { dataset, perspective, language, refId, limit, searchKeyword } = args;
	const datasetEndpoint = formatUnicorn(endpoint, { dataset });
	const queryParams = new URLSearchParams();

	if (language) {
		queryParams.append('language', language);
	}

	if (searchKeyword) {
		queryParams.append('searchKeyword', searchKeyword);
	}

	if (refId) {
		queryParams.append('refId', refId);
	}

	if (limit) {
		queryParams.append('limit', limit.toString());
	}

	if (perspectiveIsValid(perspective)) {
		queryParams.append('perspective', perspective);
	}

	return makeRequest<ApiV1.Response<ArticleRes[]>>({
		endpoint: setUrlParams(datasetEndpoint, queryParams),
		method: HTTP_METHOD.GET,
	}).then((response) => response.payload);
};

export const getArticlesEntrypoints = async (
	endpoint: string,
	args: {
		dataset: string;
		language: string;
		perspective?: ClientPerspective;
	}
) => {
	const { dataset, language, perspective } = args;
	const datasetEndpoint = formatUnicorn(endpoint, { dataset });
	const queryParams = new URLSearchParams();

	if (language) {
		queryParams.append('language', language);
	}

	if (perspectiveIsValid(perspective)) {
		queryParams.append('perspective', perspective);
	}

	return makeRequest<ApiV1.Response<EntrypointArticlesRes[]>>({
		endpoint: setUrlParams(datasetEndpoint, queryParams),
		method: HTTP_METHOD.GET,
	}).then((response) => response.payload);
};

export const getCategories = async (
	endpoint: string,
	args: {
		dataset: ApplicationType;
		perspective?: ClientPerspective;
		language: string;
	}
) => {
	const { dataset, language, perspective } = args;
	const datasetEndpoint = formatUnicorn(endpoint, { dataset });
	const queryParams = new URLSearchParams();

	if (language) {
		queryParams.append('language', language);
	}

	if (perspectiveIsValid(perspective)) {
		queryParams.append('perspective', perspective);
	}

	return makeRequest<ApiV1.Response<CategoryRes[]>>({
		endpoint: setUrlParams(datasetEndpoint, queryParams),
		method: HTTP_METHOD.GET,
	}).then((response) => response.payload);
};

export const getAuthors = async (
	endpoint: string,
	args: {
		dataset: ApplicationType;
		language: string;
		perspective?: ClientPerspective;
	}
) => {
	const { dataset, language, perspective } = args;
	const datasetEndpoint = formatUnicorn(endpoint, { dataset });
	const queryParams = new URLSearchParams();

	if (language) {
		queryParams.append('language', language);
	}

	if (perspectiveIsValid(perspective)) {
		queryParams.append('perspective', perspective);
	}

	return makeRequest<ApiV1.Response<AuthorRes[]>>({
		endpoint: setUrlParams(datasetEndpoint, queryParams),
		method: HTTP_METHOD.GET,
	}).then((response) => response.payload);
};

export const getSingleArticle = async (
	endpoint: string,
	args: {
		dataset: ApplicationType;
		slug: string;
		perspective?: ClientPerspective;
	}
) => {
	const { dataset, slug, perspective } = args;
	const queryParams = new URLSearchParams();
	const url = formatUnicorn(endpoint, { dataset, slug });

	if (perspectiveIsValid(perspective)) {
		queryParams.append('perspective', perspective);
	}

	return makeRequest<ApiV1.Response<ArticleRes>>({
		endpoint: setUrlParams(url, queryParams),
		method: HTTP_METHOD.GET,
	}).then((response) => response.payload);
};

export const getSingleCategory = async (
	endpoint: string,
	args: {
		dataset: ApplicationType;
		slug: string;
		perspective?: ClientPerspective;
	}
) => {
	const { dataset, slug, perspective } = args;
	const queryParams = new URLSearchParams();
	const url = formatUnicorn(endpoint, { dataset, slug });

	if (perspectiveIsValid(perspective)) {
		queryParams.append('perspective', perspective);
	}

	return makeRequest<ApiV1.Response<CategoryRes>>({
		endpoint: setUrlParams(url, queryParams),
		method: HTTP_METHOD.GET,
	}).then((response) => response.payload);
};

export const getAuthor = async (
	endpoint: string,
	args: {
		dataset: ApplicationType;
		slug: string;
		perspective?: ClientPerspective;
	}
) => {
	const { dataset, slug, perspective } = args;
	const queryParams = new URLSearchParams();
	const url = formatUnicorn(endpoint, { dataset, slug });

	if (perspectiveIsValid(perspective)) {
		queryParams.append('perspective', perspective);
	}

	return makeRequest<ApiV1.Response<AuthorRes>>({
		endpoint: setUrlParams(url, queryParams),
		method: HTTP_METHOD.GET,
	}).then((response) => response.payload);
};

const setUrlParams = (url: string, params: URLSearchParams) => {
	const paramsString = params.toString();
	return paramsString.length ? `${url}?${paramsString}` : url;
};

const perspectiveIsValid = (perspective: ClientPerspective | undefined) =>
	(!!perspective && perspective === 'published') || perspective === 'drafts';
